/**
 * How many players a team puts on court, and the card's members line
 * ("8 players · 6 on court"). The same sizes as the backend's teamCapacity.js:
 * a team's own max_members first, else the sport's size (never a flat 25).
 */

export const SPORT_ON_COURT = Object.freeze({
  volleyball: 6,
  basketball: 5,
  football: 11,
  soccer: 11,
  futsal: 5,
  badminton: 2,
  'table tennis': 2,
  tennis: 2,
  pickleball: 2,
  squash: 1,
  handball: 7,
  hockey: 11,
  'field hockey': 11,
  'ice hockey': 6,
  cricket: 11,
  rugby: 15,
});

const positiveInt = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

const parseSettings = (settings) => {
  if (typeof settings === 'string') {
    try {
      return JSON.parse(settings) || {};
    } catch {
      return {};
    }
  }
  return settings && typeof settings === 'object' ? settings : {};
};

/** Players on court for a sport name ("Table Tennis", "table_tennis"), or null. */
export const sportOnCourt = (sportName) =>
  SPORT_ON_COURT[String(sportName || '').toLowerCase().replace(/[_-]+/g, ' ').trim()] ?? null;

/**
 * Players on court for a team: its max_members, else its sport's size, else the
 * sport's minimum. `fallbackSport` covers rows without a sport (a league's sport).
 */
export const onCourtSize = (team = {}, fallbackSport = null) =>
  positiveInt(parseSettings(team.settings).max_members)
  ?? positiveInt(team.max_members)
  ?? sportOnCourt(team.sport_name || team.sport_type || team.sport || fallbackSport)
  ?? positiveInt(team.min_players_required)
  ?? null;

/** "8 players · 6 on court" (or "1 player" when the size is unknown). */
export const membersLine = (count, onCourt) => {
  const n = Number(count) || 0;
  const players = `${n} player${n === 1 ? '' : 's'}`;
  return onCourt ? `${players} · ${onCourt} on court` : players;
};
